// castable-effect.ts — Compiler IV (2026-09-30): the LEAN baked `effect` of one computed.creature.castable row — the port of
// ft_castable_spell_obj()'s effect block (build_castable_spells_data_inc.php, $lean_for_blob) with its two calculators
// calculate_damage_dice / calculate_damage_mod (ability_effect_speed_renderers.php:205-254).
//
// PHP bakes only what the client cannot re-derive from the spell's own layers: `dice` when the cantrip scaling moved it, `mod` when a
// placeholder ([spellcasting_essence] / [spell_level] / [<class>_level]) or Disciple of Life moved it. A spell whose numbers equal its
// raw layers bakes NO effect. A spell without a `die` layer has no effect at all.
//
// The builder's arithmetic runs inside ft_bake_castable_into_blob's try/catch — a TypeError there (a non-numeric dice under cantrip
// scaling, a list-valued mod) EMPTIES the whole castable payload. Those inputs refuse by name instead of guessing that shape.
//
// [<class>_level] placeholders stay VERBATIM: replace_class_level_placeholders() compares player_characters.class_N to the class id with
// !==, and at the bake (query_player_character_arr, after finalize) every class_N is its PROVIDER NEST, never the bare id — so the
// replacement never fires there (real-PC corpus 2026-09-30: a Priest's Divine Aid bakes "[priest_level]", or 2 + level + 0 under Disciple
// of Life). The stored row's class_N are leaned back to ids later; reading them here would replace what PHP never replaced.
import type { ItemFieldRow } from './item-field-reader';
import { isPhpNumeric, phpJsonFloat } from './php-decimal-values';
import { phpJsonTruthy } from './php-json-values';

type Row = Record<string, unknown>;
const isObj = (v: unknown): v is Row => v !== null && typeof v === 'object' && !Array.isArray(v);
const upper = (v: unknown): string => String(v ?? '').toUpperCase();

/** calculate_damage_mod's Disciple of Life passive (verbatim UUID). */
export const DISCIPLE_OF_LIFE_ID = '4E67918C-B0BE-A25D-76EB-F486B9799D03';

export interface CastableEffectReaders {
    layerValue: (row: ItemFieldRow | null, name: string) => unknown;
    hasLayer: (row: ItemFieldRow, name: string) => boolean;
}
/** The caster-side inputs, read once per compile from the FINISHED row (PHP bakes after finalize). */
export interface CastableEffectCaster {
    /** layer_value($caster_arr, 'total_level') — the computed meta bridge. */
    totalLevel: unknown;
    /** layer_value($caster_arr, 'spellcasting_essence_mod') — the computed spellcasting bridge (zero-elided → null). */
    essenceMod: unknown;
    /** A passive_ability entry of Disciple of Life that passive_ability_entry_is_active() accepts. */
    discipleOfLife: boolean;
}
export type CastableEffectResult = { ok: true; effect: Row | null } | { ok: false; reason: string };

/** ft_caster_status_choice_layers() twin (caster_status_choice.php / caster-status-choice.ts CHOICE_APPLIER_LAYERS). */
const CASTER_CHOICE_APPLIER_LAYERS = ['status_effect_applied_to_target_after_contesting_roll_failed', 'status_effect_applied_to_target_after_cast',
    'status_effect_applied_to_target_on_hit', 'status_effect_applied_to_caster_after_contesting_roll_failed', 'status_effect_applied_to_caster_after_cast',
    'status_effect_applied_to_caster_on_hit'];

/** N4c (2026-10-02): an ability_choice BRANCH's input family — determine_ability_button_input_id() (ability_button_renderer.php) minus
 *  its `input_<uuid>_` prefix, '' when the branch needs no control (ft_castable_ability_choice_branches). PHP's loose `== '[x]'` compares
 *  the layer's scalar as a string. */
export function castableInputFamily(def: ItemFieldRow, readers: CastableEffectReaders & { entryCount: (row: ItemFieldRow, name: string) => number }): string | null {
    const v = (name: string): unknown => readers.layerValue(def, name);
    const x = (name: string): boolean => { const s = v(name); return s !== null && s !== undefined && !isObj(s) && !Array.isArray(s) && String(s) === '[x]'; };
    const type = isObj((def as Row).items) ? String(((def as Row).items as Row).item_type ?? '') : '';
    if (readers.hasLayer(def, 'consumes_charges_qty') && x('consumes_charges_qty') && !['spell', 'menta', 'curse'].includes(type)) return 'variable_charges';
    // ft_spell_is_level_upcastable(): spell_upcastable AND an is_numeric level (spell_level ?? menta_level ?? 0) whose (int) is > 0 — a cantrip never
    const level = v('spell_level') ?? v('menta_level') ?? 0;
    const leveled = readers.hasLayer(def, 'spell_upcastable') && (typeof level === 'number' ? Number.isFinite(level) && Math.trunc(level) > 0
        : typeof level === 'string' && /^\s*[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?\s*$/.test(level) && Math.trunc(Number(level)) > 0);
    if (leveled || (readers.hasLayer(def, 'max_targets') && x('max_targets')) || (readers.hasLayer(def, 'consumes_charges_qty') && x('consumes_charges_qty'))) return 'spell_upcastable';
    const mana = v('change_caster_mana');
    if (x('dice') || (mana !== null && mana !== undefined && !isObj(mana) && String(mana).includes('[x]'))) return 'variable_mana_dice';
    if (readers.hasLayer(def, 'user_choice_1_summon')) return 'user_choice_1_summon';
    if (readers.hasLayer(def, 'user_choice_1_shapeshift')) return 'user_choice_1_shapeshift';
    if (readers.hasLayer(def, 'user_choice_1_essence')) return 'user_choice_1_essence';
    // status_effect_applied_is_caster_choice (2026-10-07) — PHP: ft_caster_status_choice_ids() !== [] (the flag + an applier list of 2+)
    { const f = v('status_effect_applied_is_caster_choice'); const on = Array.isArray(f) ? f[0] : f;
      if (on != null && on !== '' && on !== 0 && on !== '0' && on !== false && CASTER_CHOICE_APPLIER_LAYERS.some(l => readers.entryCount(def, l) > 1))
        return 'user_choice_1_status_effect'; }
    if (readers.entryCount(def, 'damage_type') > 1) return 'user_choice_1_damage_type';
    return '';
}

/** The caster inputs from a finished row + its passive_ability bucket entries. */
export function castableEffectCaster(row: ItemFieldRow, passives: readonly unknown[], readers: Pick<CastableEffectReaders, 'layerValue'>): CastableEffectCaster {
    const discipleOfLife = passives.some(e => {
        if (!isObj(e)) return false; // calculate_damage_mod skips a non-array entry (a bare ref) outright
        // passive_ability_entry_is_active: an unstamped entry is active; a stamped one by PHP truthiness
        if (Object.prototype.hasOwnProperty.call(e, 'passive_conditions_active') && !phpJsonTruthy(e.passive_conditions_active)) return false;
        // ability_entry_id: legacy flat ability_id, then the v21 / v23 ref slot (item_arr | item_id as a bare string)
        const id = typeof e.ability_id === 'string' && e.ability_id !== '' ? e.ability_id
            : typeof e.item_arr === 'string' && e.item_arr !== '' ? e.item_arr
            : typeof e.item_id === 'string' ? e.item_id
            : isObj(e.item_arr) && isObj((e.item_arr as Row).items) ? ((e.item_arr as Row).items as Row).item_id : '';
        return upper(id) === DISCIPLE_OF_LIFE_ID;
    });
    return { totalLevel: readers.layerValue(row, 'total_level'), essenceMod: readers.layerValue(row, 'spellcasting_essence_mod'), discipleOfLife };
}

/** A canonical signature of every caster input the effect reads — equal signatures bake equal effects for the same spell. */
export function castableEffectSignature(c: CastableEffectCaster): string {
    return JSON.stringify([c.totalLevel ?? null, c.essenceMod ?? null, c.discipleOfLife]);
}

/** PHP `$x + $n` for the cantrip scaling: int + int only (a float / non-numeric dice refuses — see the header). */
function addInt(value: unknown, n: number): number | null {
    if (value === null || value === undefined) return n;
    if (typeof value === 'number') return Number.isInteger(value) ? value + n : null;
    if (typeof value === 'string' && /^[\t\n\r\v\f ]*[+-]?\d+[\t\n\r\v\f ]*$/.test(value)) return parseInt(value.trim(), 10) + n;
    return null;
}
/** PHP intval((float) $x) for Disciple of Life's numeric mod. */
const intOfFloat = (v: unknown): number => Math.trunc(phpJsonFloat(v));
/** PHP strpos($haystack, $needle) !== false on a scalar (int / float / string; null → '' never matches). */
const contains = (v: unknown, needle: string): boolean => (typeof v === 'string' || typeof v === 'number') && String(v).includes(needle);

/** ft_castable_spell_obj's lean effect for one spell definition. */
export function bakeCastableLeanEffect(def: ItemFieldRow, caster: CastableEffectCaster, readers: CastableEffectReaders): CastableEffectResult {
    if (!readers.hasLayer(def, 'die')) return { ok: true, effect: null };
    const rawDice = readers.layerValue(def, 'dice');
    // calculate_damage_dice
    let calcDice: unknown = rawDice;
    if (readers.hasLayer(def, 'spell_scalable_cantrip')) {
        const total = phpJsonFloat(caster.totalLevel ?? 0);
        const step = total >= 33 ? 3 : total >= 19 ? 2 : total >= 9 ? 1 : 0;
        if (step) {
            const sum = addInt(rawDice, step);
            if (sum === null) return { ok: false, reason: 'unported:castable:effect-dice' };
            calcDice = sum;
        }
    }
    // calculate_damage_mod
    const rawMod = readers.layerValue(def, 'mod');
    let calcMod: unknown = null;
    if (rawMod !== null && rawMod !== undefined) {
        if (typeof rawMod === 'object' || typeof rawMod === 'boolean') return { ok: false, reason: 'unported:castable:effect-mod' };
        let mod: unknown = rawMod;
        if (contains(mod, '[spellcasting_essence]')) mod = caster.essenceMod ?? 0;
        if (contains(mod, '[spell_level]')) mod = readers.layerValue(def, 'spell_level') ?? 0;
        if (caster.discipleOfLife) {
            const level = readers.layerValue(def, 'spell_level');
            mod = 2 + (level === null || level === undefined ? 0 : intOfFloat(level)) + (isPhpNumeric(mod) ? intOfFloat(mod) : 0);
        }
        calcMod = mod;
    }
    // the lean: bake only what the def's raw numeric layers do not reproduce
    const lean: Row = {};
    const blank = (v: unknown): boolean => v === null || v === undefined || v === '';
    const diceReproduces = isPhpNumeric(calcDice) && ((isPhpNumeric(rawDice) && phpJsonFloat(calcDice) === phpJsonFloat(rawDice))
        || (blank(rawDice) && phpJsonFloat(calcDice) === 0));
    if (!diceReproduces) lean.dice = calcDice ?? null;
    const modReproduces = ((calcMod === null) && (rawMod === null || rawMod === undefined))
        || (isPhpNumeric(calcMod) && ((isPhpNumeric(rawMod) && phpJsonFloat(calcMod) === phpJsonFloat(rawMod)) || (blank(rawMod) && phpJsonFloat(calcMod) === 0)));
    if (!modReproduces) lean.mod = calcMod;
    return { ok: true, effect: Object.keys(lean).length ? lean : null };
}
